import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { ComplianceService } from '@/services/compliance.service';
import { ComplianceStatus } from '@/models/compliance.model';
import { format } from 'date-fns';

export const Compliance: React.FC = () => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'records' | 'policies' | 'audits' | 'incidents'>('records');

  const { data: complianceRecords = [], isLoading: recordsLoading } = useQuery({
    queryKey: ['complianceRecords'],
    queryFn: () => ComplianceService.getAllComplianceRecords(),
    enabled: !!profile && activeTab === 'records',
  });

  const { data: policies = [], isLoading: policiesLoading } = useQuery({
    queryKey: ['policies'],
    queryFn: () => ComplianceService.getAllPolicies(),
    enabled: !!profile && activeTab === 'policies',
  });

  const { data: audits = [], isLoading: auditsLoading } = useQuery({
    queryKey: ['audits'],
    queryFn: () => ComplianceService.getAllAudits(),
    enabled: !!profile && activeTab === 'audits',
  });

  const { data: incidents = [], isLoading: incidentsLoading } = useQuery({
    queryKey: ['complianceIncidents'],
    queryFn: () => ComplianceService.getAllIncidents(),
    enabled: !!profile && activeTab === 'incidents',
  });

  const tabs = [
    { id: 'records' as const, name: 'Compliance Records', count: complianceRecords.length },
    { id: 'policies' as const, name: 'Policies', count: policies.length },
    { id: 'audits' as const, name: 'Audits', count: audits.length },
    { id: 'incidents' as const, name: 'Incidents', count: incidents.length },
  ];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      compliant: 'bg-green-100 text-green-800',
      non_compliant: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800',
      under_review: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      archived: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      reported: 'bg-yellow-100 text-yellow-800',
      under_investigation: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800',
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  // Stats
  const compliantCount = complianceRecords.filter(r => r.status === ComplianceStatus.compliant).length;
  const nonCompliantCount = complianceRecords.filter(r => r.status === ComplianceStatus.nonCompliant).length;
  const pendingCount = complianceRecords.filter(r => r.status === ComplianceStatus.pending).length;
  const activePolicies = policies.filter(p => p.status === 'active').length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Compliance Management</h1>
        <p className="mt-2 text-gray-600">Manage compliance records, policies, audits, and incidents</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Compliant</div>
          <div className="text-2xl font-bold text-green-600">{compliantCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Non-Compliant</div>
          <div className="text-2xl font-bold text-red-600">{nonCompliantCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Pending</div>
          <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active Policies</div>
          <div className="text-2xl font-bold text-blue-600">{activePolicies}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Compliance Records Tab */}
      {activeTab === 'records' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Compliance Records</h2>
            <Link
              to="/compliance/records/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Add Record
            </Link>
          </div>

          {recordsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading compliance records...</p>
            </div>
          ) : complianceRecords.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No compliance records found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {complianceRecords.map((record) => (
                  <li key={record.id}>
                    <Link
                      to={`/compliance/records/${record.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {record.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                              {record.status}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              {record.type}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {record.description}
                            {record.dueDate && ` • Due: ${format(record.dueDate, 'MMM d, yyyy')}`}
                          </p>
                        </div>
                      </div>
                    </Link>
                    </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Policies Tab */}
      {activeTab === 'policies' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Policies</h2>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
              + Create Policy
            </button>
          </div>

          {policiesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading policies...</p>
            </div>
          ) : policies.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No policies found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {policies.map((policy) => (
                  <li key={policy.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {policy.title}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(policy.status)}`}>
                              {policy.status}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              v{policy.version}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {policy.category} • Effective: {format(policy.effectiveDate, 'MMM d, yyyy')}
                            {policy.requiresAcknowledgment && ' • Requires Acknowledgment'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Audits Tab */}
      {activeTab === 'audits' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Audits</h2>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
              + Schedule Audit
            </button>
          </div>

          {auditsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading audits...</p>
            </div>
          ) : audits.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No audits found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {audits.map((audit) => (
                  <li key={audit.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {audit.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(audit.status)}`}>
                              {audit.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {audit.type} • Scheduled: {format(audit.scheduledDate, 'MMM d, yyyy')}
                            {audit.findings && audit.findings.length > 0 && ` • ${audit.findings.length} findings`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Incidents Tab */}
      {activeTab === 'incidents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Compliance Incidents</h2>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
              + Report Incident
            </button>
          </div>

          {incidentsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading incidents...</p>
            </div>
          ) : incidents.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No incidents found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {incidents.map((incident) => (
                  <li key={incident.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {incident.title}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                              {incident.severity}
                            </span>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>
                              {incident.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {incident.type} • Reported: {format(incident.reportedAt, 'MMM d, yyyy')} by {incident.reportedByName}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
